import { CookieService } from 'ngx-cookie-service';
import {
  LocalizedProcess,
  KnownProcessType,
  ProcessStage,
} from '../../models/process-models';
import { Component, OnInit } from '@angular/core';
import { ProcessService } from '../../services/process.service';
import { ProcessParticipationService } from '../../services/process.participation.service';
import { ProcessFilter } from './process-filter.model';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { NamedEntity } from '../../models/named-entity.model';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { BusinessUnitService } from '../../services/business-unit.service';
import { BusinessUnit } from '../../models/identity';
import { HttpParams } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { getWithoutTime } from '../../helpers/date-helper';

@Component({
  selector: 'app-processes-list',
  templateUrl: './processes-list.component.html',
  styleUrls: ['./processes-list.component.scss'],
})
export class ProcessesListComponent implements OnInit {
  dateFormat = 'YYYYMMDD';
  processes: Array<LocalizedProcess> = [];
  selectorProcesses: Array<LocalizedProcess> = [];
  knownProcessType = KnownProcessType;
  processStage = ProcessStage;
  processFilter: ProcessFilter = new ProcessFilter();
  businessUnits: Array<BusinessUnit> = [];
  employees: Array<NamedEntity> = [];
  respondents: Array<NamedEntity> = [];
  grades: Array<NamedEntity> = [];
  isSpinnerEnable: boolean;

  private readonly processFilterCookieName = 'ProcessFilter';

  constructor(
    private readonly processParticipationService: ProcessParticipationService,
    private readonly businessUnitservice: BusinessUnitService,
    private readonly cookieService: CookieService,
    private readonly configService: ConfigService,
    private readonly processService: ProcessService,
    private readonly router: Router,
    private readonly userService: UserService
  ) {}

  ngOnInit() {
    this.loadFilters();
    this.loadFiltersCollections();
    if (!this.processFilter.processTypes) this.processFilter.processTypes = [];
    this.userService.getGrades().subscribe((r) => (this.grades = r));
    this.getByFilters();
  }

  onStageChanged(processStage: ProcessStage) {
    this.processFilter.stage = processStage;
    this.onFilterChanged();
  }

  onDatepickerCleared() {
    this.processFilter.startDate = null;
    this.onFilterChanged();
  }

  onFilterChanged() {
    this.selectorProcesses = this.processes.slice();

    if (this.processFilter.startDate) {
      this.selectorProcesses = this.selectorProcesses.filter((p) =>
        this.getIsEqual(new Date(p.startDate), this.processFilter.startDate)
      );
    }

    if (this.processFilter.stage != null) {
      this.selectorProcesses = this.selectorProcesses.filter(
        (p) => p.stage === this.processFilter.stage
      );
    }

    if (this.processFilter.processTypes.length !== 0) {
      this.selectorProcesses = this.selectorProcesses.filter((p) =>
        p.knownProcessTypes.some(
          (t) => this.processFilter.processTypes.indexOf(t) !== -1
        )
      );
    }

    if (this.processFilter.surveyName) {
      const surveyName = this.processFilter.surveyName.toLowerCase();
      this.selectorProcesses = this.selectorProcesses.filter(
        (p) => p.surveyName.toLowerCase().indexOf(surveyName) !== -1
      );
    }

    this.saveFilters();
  }

  getSelectedProcesses() {
    return this.selectorProcesses.filter((p) => p.isSelected);
  }

  clearAll() {
    return this.selectorProcesses.forEach((p) => {
      p.isSelected = false;
    });
  }

  toggleProcess(process: LocalizedProcess) {
    process.isSelected = !process.isSelected;
  }

  get isSelectedProcesses(): boolean {
    return this.processes.some((p) => p.isSelected);
  }

  navigateToProcess(process: LocalizedProcess) {
    process.stage === ProcessStage.Draft
      ? this.router.navigate(['/admin/process-builder', { id: process.id }])
      : this.router.navigate([
          '/admin/detailed-process',
          { id: process.id, durationId: process.durationId },
        ]);
  }

  getIsImportBtnEnabled() {
    let selected = this.getSelectedProcesses();
    return (
      selected.length > 0 &&
      selected.every(
        (p) =>
          p.stage != ProcessStage.Completed && p.stage != ProcessStage.Cancelled
      )
    );
  }

  getIsExportBtnEnabled() {
    return this.isSelectedProcesses;
  }

  onFileAdded(event: any) {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    const file: File = event.target.files[0];
    this.processParticipationService
      .updateNotCompletedByProcesses(file, this.getSelectedProcesses())
      .subscribe(() => this.getByFilters());
  }

  exportSelectedProcess() {
    const httpParams = this.createSelectedDurationHttpParams();
    this.processParticipationService
      .exportSelectedByProcessIds(httpParams)
      .subscribe((data) =>
        saveAs(data, `Exported_respondents_${new Date().toISOString()}.xlsx`)
      );
  }

  private createSelectedDurationHttpParams(): HttpParams {
    const selectedProcesses = this.getSelectedProcesses();
    if (!selectedProcesses) {
      return;
    }
    return this.configService.appendHttpParams(
      new HttpParams(),
      'durationIds',
      selectedProcesses.map((sp) => sp.durationId)
    );
  }

  toggleProcessTypeFilter(processType: KnownProcessType) {
    this.processService.toggleEnumSetting(
      this.processFilter.processTypes,
      processType
    );
    this.onFilterChanged();
  }

  async onEmployeeFilterTyped(employeePattern: string) {
    let searchResult = await this.searchEmployees(employeePattern);
    if (searchResult.length > 0) {
      this.employees = searchResult;
    }
  }

  async onRespondentFilterTyped(respondentPattern: string) {
    let searchResult = await this.searchEmployees(respondentPattern);
    if (searchResult.length > 0) {
      this.respondents = searchResult;
    }
  }

  private async searchEmployees(pattern: string) {
    if (!this.getIsStartSearch(pattern)) {
      return [];
    }
    return await this.userService.getBySearchPattern(pattern).toPromise();
  }

  onBusinessUnitFilterTyped(pattern: string) {
    if (!this.getIsStartSearch(pattern)) {
      return;
    }
    this.businessUnitservice
      .getBusinessUnitInfo(pattern)
      .subscribe((r) => (this.businessUnits = r));
  }

  onEmployeeFilterSelected(employeeId: number) {
    this.processFilter.employeeId = employeeId;
    this.getByFilters();
  }

  onRespondentFilterSelected(respondentId: number) {
    this.processFilter.respondentId = respondentId;
    this.getByFilters();
  }

  onBusinessUnitFilterSelected(businessUnitId: number) {
    this.processFilter.businessUnitId = businessUnitId;
    this.getByFilters();
  }

  onGradeFilterSelected(gradeId: number) {
    this.processFilter.gradeId = gradeId;
    this.getByFilters();
  }

  setSpinnerEnabled(isEnabled: boolean) {
    this.isSpinnerEnable = isEnabled;
  }

  private getByFilters() {
    this.setSpinnerEnabled(true);
    this.processService
      .getByFilters(this.processFilter)
      .subscribe((results) => {
        results.sort((a, b) => {
          let comparation =
            this.getTime(b.startDate) - this.getTime(a.startDate);
          return comparation != 0 ? comparation : b.id - a.id;
        });
        this.selectorProcesses = results;
        this.processes = results;
        this.onFilterChanged();
        this.setSpinnerEnabled(false);
      });
  }

  private getTime(date?: Date) {
    date = getWithoutTime(date);
    return date != null ? date.getTime() : 0;
  }

  private getIsStartSearch(pattern: string): boolean {
    return (
      pattern.length >= this.configService.configData.minimumSearchStringLength
    );
  }

  private getIsEqual(d1: Date, d2: Date): boolean {
    return (
      moment(d1).format(this.dateFormat) === moment(d2).format(this.dateFormat)
    );
  }

  private loadFilters() {
    if (this.cookieService.check(this.processFilterCookieName)) {
      const cookie = this.cookieService.get(this.processFilterCookieName);
      if (cookie) {
        this.processFilter = JSON.parse(cookie);
      }
    }
  }

  private loadFiltersCollections() {
    if (this.processFilter.businessUnitId) {
      this.businessUnitservice
        .getBusinessUnit(this.processFilter.businessUnitId)
        .subscribe((r) => (this.businessUnits = [r]));
    }

    if (this.processFilter.employeeId) {
      this.userService
        .getEmployee(this.processFilter.employeeId)
        .subscribe((r) => (this.employees = [r]));
    }

    if (this.processFilter.respondentId) {
      this.userService
        .getEmployee(this.processFilter.respondentId)
        .subscribe((r) => (this.respondents = [r]));
    }

    // Workaround for bsDatepicker
    if (this.processFilter.startDate) {
      this.processFilter.startDate = new Date(this.processFilter.startDate);
    }
  }

  private saveFilters() {
    const value = JSON.stringify(this.processFilter);
    let expirationDate = new Date();
    expirationDate.setHours(expirationDate.getHours() + 3);
    this.cookieService.set(
      this.processFilterCookieName,
      value,
      expirationDate,
      'admin'
    );
  }
}
